import React, { useState, useMemo } from 'react';
import { Modal, Button, Input, Empty, Badge, Space, Radio } from 'antd';
import { FilterOutlined, SearchOutlined, UnorderedListOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { volcano } from '@ant-design/colors';
import LocationMapSelector from './LocationMapSelector';
import LocationListGrouped from './LocationListGrouped';

const ACTIVE_COLOR = volcano.primary;

const FilterModal = ({ visible, onClose, options, activeFilters, onApplyFilters }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [tempFilters, setTempFilters] = useState(activeFilters || {});
  const [locationViewMode, setLocationViewMode] = useState('list'); // 'list' or 'map'

  // Filter categories available
  const categories = useMemo(() => {
    return Object.keys(options || {}).filter(key => options[key] && Array.isArray(options[key]));
  }, [options]);

  // Get filtered items for the selected category
  const filteredItems = useMemo(() => {
    if (!selectedCategory || !options[selectedCategory]) return [];
    
    const items = options[selectedCategory];
    if (!searchText) return items;
    
    return items.filter(item => 
      item.name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [selectedCategory, options, searchText]);

  // Count total active filters
  const totalActiveFilters = useMemo(() => {
    return Object.values(tempFilters).reduce((sum, arr) => sum + (arr?.length || 0), 0);
  }, [tempFilters]);

  // Toggle a filter item
  const toggleFilter = (category, item) => {
    setTempFilters(prev => {
      const categoryFilters = prev[category] || [];
      const filterValue = item.name;
      const isActive = categoryFilters.includes(filterValue);
      
      return {
        ...prev,
        [category]: isActive
          ? categoryFilters.filter(f => f !== filterValue)
          : [...categoryFilters, filterValue]
      };
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setTempFilters({});
  };

  // Apply filters and close
  const handleApply = () => {
    onApplyFilters(tempFilters);
    onClose();
  };

  // Reset to active filters when closing without applying
  const handleCancel = () => {
    setTempFilters(activeFilters || {});
    setSelectedCategory(null);
    setSearchText('');
    onClose();
  };

  // When category is selected, clear search
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSearchText('');
  };

  return (
    <Modal
      title={
        <Space style={{color: '#eee'}}>
          <FilterOutlined />
          <span>Filter Photos</span>
          {totalActiveFilters > 0 && (
            <Badge count={totalActiveFilters} style={{ backgroundColor: ACTIVE_COLOR }} />
          )}
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      width="90vw"
      style={{ maxWidth: '1000px' }}
      footer={[
        <Button key="clear" onClick={clearAllFilters} disabled={totalActiveFilters === 0}>
          Clear All
        </Button>,
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="apply" type="primary" onClick={handleApply} style={{ backgroundColor: ACTIVE_COLOR, borderColor: ACTIVE_COLOR }}>
          Apply Filters
        </Button>,
      ]}
      styles={{
        body: { minHeight: '400px', maxHeight: '70vh', padding: '16px' },
        content: { background: '#1e1e1ef0', color: '#fff' },
        header: { background: '#1e1e1ef0' },
        title: { color: '#fff' },
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Category Selection - Horizontal Scroll */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ marginBottom: '8px', fontWeight: 'bold', color: '#888', fontSize: '12px' }}>
            FILTER BY
          </div>
          <div style={{ 
            display: 'flex',
            gap: '8px',
            overflowX: 'auto',
            paddingBottom: '8px',
            WebkitOverflowScrolling: 'touch'
          }}>
            {categories.map(category => {
              const categoryCount = tempFilters[category]?.length || 0;
              const isSelected = selectedCategory === category;
              return (
                <Button
                  key={category}
                  onClick={() => handleCategorySelect(category)}
                  style={{ 
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    backgroundColor: isSelected ? ACTIVE_COLOR : '#2a2a2a',
                    color: isSelected ? '#fff' : '#aaa',
                    border: isSelected ? `1px solid ${ACTIVE_COLOR}` : '1px solid #444',
                    transition: 'all 0.3s'
                  }}
                >
                  <span style={{ textTransform: 'capitalize' }}>{category}</span>
                  {categoryCount > 0 && (
                    <Badge 
                      count={categoryCount} 
                      style={{ 
                        backgroundColor: volcano.primary,
                        marginLeft: '8px'
                      }} 
                    />
                  )}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Item Selection - Vertical Stack */}
        <div style={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '0'
        }}>
          {!selectedCategory ? (
            <Empty 
              description="Select a category to filter"
              style={{ marginTop: '60px' }}
            />
          ) : (
            <>
              {/* View Mode Toggle (only for location) and Search Bar */}
              <div style={{ marginBottom: '12px', display: 'flex', gap: '8px' }}>
                {selectedCategory === 'location' && (
                  <Radio.Group 
                    value={locationViewMode} 
                    onChange={e => setLocationViewMode(e.target.value)}
                    style={{ marginRight: '8px' }}
                    buttonStyle="solid"
                  >
                    <Radio.Button value="list" style={{ color: locationViewMode === 'list' ? '#fff' : '#aaa', backgroundColor: locationViewMode === 'list' ? ACTIVE_COLOR : '#2a2a2a', borderColor: ACTIVE_COLOR }}>
                      <UnorderedListOutlined /> List
                    </Radio.Button>
                    <Radio.Button value="map" style={{ color: locationViewMode === 'map' ? '#fff' : '#aaa', backgroundColor: locationViewMode === 'map' ? ACTIVE_COLOR : '#2a2a2a', borderColor: ACTIVE_COLOR }}>
                      <EnvironmentOutlined /> Map
                    </Radio.Button>
                  </Radio.Group>
                )}
                {locationViewMode === 'list' && (
                  <Input
                    prefix={<SearchOutlined style={{ color: '#888' }} />}
                    placeholder={`Search ${selectedCategory}...`}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    allowClear
                    style={{ 
                      flex: 1,
                      backgroundColor: '#2a2a2a',
                      color: '#fff',
                      border: '1px solid #444'
                    }}
                  />
                )}
              </div>

              {/* Items Grid or Map */}
              <div style={{ 
                flex: 1,
                overflowY: 'auto',
                paddingRight: '4px'
              }}>
                {selectedCategory === 'location' && locationViewMode === 'map' ? (
                  <LocationMapSelector
                    locations={options.location || []}
                    selectedLocations={tempFilters.location || []}
                    onToggleLocation={(location) => toggleFilter('location', location)}
                  />
                ) : selectedCategory === 'location' && locationViewMode === 'list' ? (
                  <LocationListGrouped
                    locations={options.location || []}
                    selectedLocations={tempFilters.location || []}
                    onToggleLocation={(location) => toggleFilter('location', location)}
                    searchText={searchText}
                  />
                ) : filteredItems.length === 0 ? (
                  <Empty 
                    description={searchText ? 'No results found' : 'No items available'}
                    style={{ marginTop: '40px' }}
                  />
                ) : (
                  <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: '8px'
                  }}>
                    {filteredItems.map(item => {
                      const isActive = tempFilters[selectedCategory]?.includes(item.name);
                      return (
                        <Button
                          key={item.id}
                          onClick={() => toggleFilter(selectedCategory, item)}
                          style={{ 
                            height: 'auto',
                            padding: '12px 8px',
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            backgroundColor: isActive ? ACTIVE_COLOR : '#2a2a2a',
                            color: isActive ? '#fff' : '#aaa',
                            border: isActive ? `1px solid ${ACTIVE_COLOR}` : '1px solid #444',
                            transition: 'all 0.3s'
                          }}
                          title={item.name}
                        >
                          {item.name}
                        </Button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

    </Modal>
  );
};

export default FilterModal;
